import { useState } from "react";
import { useLocation, useHistory, Link } from "react-router-dom";
import {
  Drawer,
  Toolbar,
  List,
  Divider,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  AppBar,
  Box,
} from "@mui/material";
import {
  People as PeopleIcon,
  Menu as MenuIcon,
  Add as AddIcon,
  FindInPage as FindInPageIcon,
  TransitEnterexit as TransitEnterexitIcon,
  Assessment as AssessmentIcon,
  Summarize as SummarizeIcon,
  NoteAdd as NoteAddIcon,
  Storage as StorageIcon,
  Settings as SettingsIcon,
  DateRange as ScheduleIcon,
} from "@mui/icons-material";
import useDialog from "../../hooks/useDialog";

const drawerWidth = 240;
const drawerWidthMd = 210;

function SideBar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const history = useHistory();
  const { promptConfirm } = useDialog();

  const userRole = localStorage.getItem("role");
  
  // Safe parsing for agency department
  const agencyDepartment = (() => {
    try {
      return JSON.parse(localStorage.getItem("agency-department"));
    } catch (e) {
      console.error("Failed to parse agency department", e);
      return null;
    }
  })();
  const isAdminDepartment = agencyDepartment?.title === "ადმინისტრაციული დეპარტამენტი";

  // Safe parsing for permissions
  const permissions = (() => {
    try {
      return JSON.parse(localStorage.getItem("permissions")) || [];
    } catch {
      return [];
    }
  })();

  // Menu Definitions
  const rawMenuItems = [
    {
      name: "მთავარი გვერდი",
      link: "/",
      icon: <FindInPageIcon />,
      visible: !["ROLE_MONITORING", "ROLE_MONITORING_CGS", "ROLE_MONITORING_REVIEW"].includes(userRole) && !isAdminDepartment,
    },
    {
      name: "სესხის/ლიზინგის დამატება",
      link: "/add-application",
      icon: <AddIcon />,
      visible: userRole === "ROLE_BANK",
    },
    {
      name: "გრაფიკების მართვა",
      link: "/scheduling",
      icon: <ScheduleIcon />,
      visible: userRole === "ROLE_BANK" && permissions.includes("ADD_USER"),
    },
    {
      name: "რაპორტი",
      link: "/report",
      icon: <AssessmentIcon />,
      visible:
        (userRole === "ROLE_AGENCY" && !isAdminDepartment) ||
        ["ROLE_MONITORING", "ROLE_MONITORING_CGS", "ROLE_MONITORING_REVIEW"].includes(userRole) ||
        (userRole === "ROLE_BANK" && permissions.includes("ADD_USER")),
    },
    {
      name: "მონიტორინგი",
      link: "/monitoring",
      icon: <SummarizeIcon />,
      visible: userRole !== "ROLE_BANK" && !isAdminDepartment,
    },
    {
      name: "მონიტორინგის ფორმა",
      link: "/monitoring-form",
      icon: <NoteAddIcon />,
      visible: userRole === "ROLE_MONITORING",
    },
    {
      name: "მონაცემთა მართვა",
      link: "/data-management",
      icon: <StorageIcon />,
      visible: userRole === "ROLE_AGENCY",
    },
  ];

  const rawSubMenuItems = [
    {
      name: "მომხმარებლები",
      link: "/users",
      icon: <PeopleIcon />,
      visible: !isAdminDepartment,
    },
    {
      name: "პარამეტრები",
      link: "/settings",
      icon: <SettingsIcon />,
      visible: true,
    },
  ];

  const menuItems = rawMenuItems.filter((item) => item.visible);
  const subMenuItems = rawSubMenuItems.filter((item) => item.visible);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  async function logout() {
    const confirmed = await promptConfirm({
      title: "გასვლა",
      message: "გსურთ სისტემიდან გასვლა?",
    });

    if (!confirmed) return;
    
    localStorage.clear();
    history.push("/");
    window.location.reload();
  }

  const drawerContent = (
    <div>
      <Box
        component="img"
        src="/logo.png"
        alt="logo"
        sx={{
          width: "80%",
          margin: "30px auto 0px auto",
          display: "block",
        }}
      />
      <Toolbar />
      <List sx={{ color: "#e0e0e0" }}>
        {menuItems.map((item, index) => (
          <ListItem key={index} disablePadding>
            <ListItemButton
              component={Link}
              to={item.link}
              selected={location.pathname === item.link}
              sx={{
                "&.Mui-selected": {
                  backgroundColor: "rgba(255, 255, 255, 0.08)",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.12)",
                  },
                },
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.04)",
                },
              }}
            >
              <ListItemIcon sx={{ color: "#e0e0e0" }}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.name} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.12)" }} />
      <List sx={{ color: "#e0e0e0" }}>
        {subMenuItems.map((item, index) => (
          <ListItem key={index} disablePadding>
            <ListItemButton
              component={Link}
              to={item.link}
              selected={location.pathname === item.link}
              sx={{
                "&.Mui-selected": {
                  backgroundColor: "rgba(255, 255, 255, 0.08)",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.04)",
                  },
                },
              }}
            >
              <ListItemIcon sx={{ color: "#e0e0e0" }}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.name} />
            </ListItemButton>
          </ListItem>
        ))}
        <ListItem disablePadding>
          <ListItemButton onClick={logout}>
            <ListItemIcon sx={{ color: "#e0e0e0" }}>
              <TransitEnterexitIcon />
            </ListItemIcon>
            <ListItemText primary="სისტემიდან გასვლა" />
          </ListItemButton>
        </ListItem>
      </List>
    </div>
  );

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          display: { md: "none" },
          width: "100%",
        }}
      >
        <Toolbar>
          <IconButton color="inherit" aria-label="open drawer" edge="start" onClick={handleDrawerToggle}>
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { md: drawerWidthMd, lg: drawerWidth }, flexShrink: { md: 0 } }}
      >
        {/* Mobile Drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              backgroundColor: "#2c5b94",
            },
            // for print, hide sidebar
            "@media print": {
              display: "none !important",
            },
          }}
        >
          {drawerContent}
        </Drawer>
        
        {/* Desktop Drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: { md: drawerWidthMd, lg: drawerWidth },
              backgroundColor: "#2c5b94",
            },
            // for print, hide sidebar
            "@media print": {
              display: "none !important",
            },
          }}
          open
        >
          {drawerContent}
        </Drawer>
      </Box>
    </>
  );
}

export default SideBar;
